/**
 * cmblLangCode: needs to be a unique id, which is going to be persisted in localstorage, or used to resolve
 * a language
 * ngxTranslateLocale: as its name denotes, it should be the locale which is going to be passed to
 * ngx-translate service when changing lang
 * flag: flag code representation for this lang (I know this is not a good thing to represent langs with flags but 🤷)
 *   Here are the guidelines to integrate a new flag into CMAWEB:
 *   - Open CMA's bower_components/flag-icon-css/flags/4x3/ directory
 *   - Locate your lang's country flag inside this folder
 *   - Copy/paste svg file in CMAWEB's src/assets/flags directory
 *   - Edit svg file and add a width="24px" height="24px" attribute values into ths <svg> root tag
 *   - Reference your svg file code into the "flag" attribute below
 * labelKey: Code used by 4i18n to load json files corresponding to selected lang
 */
const LANGUAGES = [
  { cmblLangCode: 'bg_bg', flag: 'bg', ngxTranslateLocale: 'bg', labelKey: 'bg', momentCode: 'bg' },
  { cmblLangCode: 'cs_cz', flag: 'cz', ngxTranslateLocale: 'cz', labelKey: 'cz', momentCode: 'cs' },
  { cmblLangCode: 'de_de', flag: 'de', ngxTranslateLocale: 'de', labelKey: 'de', momentCode: 'de' },
  { cmblLangCode: 'el_gr', flag: 'gr', ngxTranslateLocale: 'gr', labelKey: 'gr', momentCode: 'el' },
  { cmblLangCode: 'en_gb', flag: 'gb', ngxTranslateLocale: 'en', labelKey: 'en', momentCode: 'en' },
  { cmblLangCode: 'es_es', flag: 'es', ngxTranslateLocale: 'es', labelKey: 'es', momentCode: 'es' },
  { cmblLangCode: 'fi_fi', flag: 'fi', ngxTranslateLocale: 'fi', labelKey: 'fi', momentCode: 'fi' },
  { cmblLangCode: 'fr_fr', flag: 'fr', ngxTranslateLocale: 'fr', labelKey: 'fr', momentCode: 'fr' },
  { cmblLangCode: 'hu_hu', flag: 'hu', ngxTranslateLocale: 'hu', labelKey: 'hu', momentCode: 'hu' },
  { cmblLangCode: 'it_it', flag: 'it', ngxTranslateLocale: 'it', labelKey: 'it', momentCode: 'it' },
  { cmblLangCode: 'lt_lt', flag: 'lt', ngxTranslateLocale: 'lt', labelKey: 'lt', momentCode: 'lt' },
  { cmblLangCode: 'nl_nl', flag: 'nl', ngxTranslateLocale: 'nl', labelKey: 'nl', momentCode: 'nl' },
  { cmblLangCode: 'pl_pl', flag: 'pl', ngxTranslateLocale: 'pl', labelKey: 'pl', momentCode: 'pl' },
  { cmblLangCode: 'pt_pt', flag: 'pt', ngxTranslateLocale: 'pt', labelKey: 'pt', momentCode: 'pt' },
  { cmblLangCode: 'ro_ro', flag: 'ro', ngxTranslateLocale: 'ro', labelKey: 'ro', momentCode: 'ro' },
  { cmblLangCode: 'ru_ru', flag: 'ru', ngxTranslateLocale: 'ru', labelKey: 'ru', momentCode: 'ru' },
  { cmblLangCode: 'sk_sk', flag: 'sk', ngxTranslateLocale: 'sk', labelKey: 'sk', momentCode: 'sk' },
  { cmblLangCode: 'sv_se', flag: 'se', ngxTranslateLocale: 'sv', labelKey: 'sv', momentCode: 'sv' },
  { cmblLangCode: 'tr_tr', flag: 'tr', ngxTranslateLocale: 'tr', labelKey: 'tr', momentCode: 'tr' },
  { cmblLangCode: 'uk_ua', flag: 'ua', ngxTranslateLocale: 'uk', labelKey: 'uk', momentCode: 'uk' },
] as const;

export type FullyTypeSafeLanguages = typeof LANGUAGES;
export type Language = FullyTypeSafeLanguages[number]

export type NGXTranslateLocale = Language['ngxTranslateLocale'];
export type LanguageLabelKey = Language['labelKey'];

export type CMBLLangCode = Language['cmblLangCode'];
export type LanguageByCMBLLangCode<L extends CMBLLangCode> = Extract<Language, { cmblLangCode: L }>;
function findLanguageByCMBLLangCode<L extends CMBLLangCode>(cmblLangCode: L) {
  return LANGUAGES.find(lang => lang.cmblLangCode === cmblLangCode) as LanguageByCMBLLangCode<L>;
}

export class Languages {
  public static values() {
    return Array.from(LANGUAGES);
  }

  public static getNgxTranslateLocales(): NGXTranslateLocale[] {
    return Languages.values().map(l => l.ngxTranslateLocale);
  }

  public static fromLocale<L extends CMBLLangCode>(locale: L) {
    return findLanguageByCMBLLangCode(locale);
  }

  public static getDefaultLanguage() {
    return findLanguageByCMBLLangCode('en_gb');
  }

  public static isKnownLocaleLang(localeCandidate: string): localeCandidate is CMBLLangCode {
    return Languages.values().map(l => l.cmblLangCode.toLowerCase()).indexOf(localeCandidate) !== -1;
  }

  public static resolveFromLocaleOrFallbackOnDefault(localeCandidate: string) {
    if (Languages.isKnownLocaleLang(localeCandidate)) {
      return Languages.fromLocale(localeCandidate);
    } else {
      return Languages.getDefaultLanguage();
    }
  }
}
